import { useMemo, useState } from 'react';
import { ChevronsLeft, ChevronsRight, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  flatCatalog,
  entityLabel,
  permissionId,
  entityMatchesApp,
  type Action,
} from '../permissionCatalog';

export interface PermissionItem {
  entity: string;
  action: Action;
}

interface Props {
  /** Selected permissions (assigned). */
  value: PermissionItem[];
  onChange: (next: PermissionItem[]) => void;
  /**
   * Current app context. When provided, only entities relevant to this app
   * (plus core access-manager entities) are shown in both columns. Permissions
   * for other apps stay on the role but are managed from their own app.
   */
  app?: string | null;
  /** Optional read-only mode. */
  disabled?: boolean;
}

export function PermissionDualList({ value, onChange, app, disabled }: Props) {
  const [filterAvail, setFilterAvail] = useState('');
  const [filterAssigned, setFilterAssigned] = useState('');
  const [selectedAvail, setSelectedAvail] = useState<Set<string>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set());

  // Catalog scoped to the current app. Legacy entities present in `value` that
  // are still in-scope (same app or core) are surfaced under "Other / Legacy".
  // Entities for OTHER apps are filtered out of the editor entirely — they stay
  // on the role but live in the preserved bucket managed by the parent dialog.
  const allItems = useMemo(() => {
    const base = flatCatalog(app);
    const known = new Set(base.map((p) => p.entity));
    const legacyEntities = new Set(
      value
        .map((v) => v.entity)
        .filter((e) => !known.has(e) && entityMatchesApp(e, app)),
    );
    const legacy = [...legacyEntities].flatMap((entity) =>
      (['show', 'list', 'edit', 'create', 'delete', 'full', 'deploy'] as Action[]).map(
        (action) => ({ entity, action, group: 'Other / Legacy' }),
      ),
    );
    return [...base, ...legacy];
  }, [value, app]);

  const assignedSet = useMemo(
    () => new Set(value.map((v) => permissionId(v.entity, v.action))),
    [value],
  );

  const availItems = useMemo(
    () => allItems.filter((p) => !assignedSet.has(permissionId(p.entity, p.action))),
    [allItems, assignedSet],
  );

  const assignedItems = useMemo(
    () => allItems.filter((p) => assignedSet.has(permissionId(p.entity, p.action))),
    [allItems, assignedSet],
  );

  function matches(p: { entity: string; action: string }, query: string): boolean {
    if (!query) return true;
    const q = query.toLowerCase();
    return p.entity.toLowerCase().includes(q) || p.action.toLowerCase().includes(q);
  }

  const availFiltered = availItems.filter((p) => matches(p, filterAvail));
  const assignedFiltered = assignedItems.filter((p) => matches(p, filterAssigned));

  function move(toAssigned: boolean) {
    if (disabled) return;
    const ids = toAssigned ? selectedAvail : selectedAssigned;
    if (ids.size === 0) return;

    if (toAssigned) {
      const next = [...value];
      ids.forEach((id) => {
        const [entity, action] = id.split('::');
        next.push({ entity, action: action as Action });
      });
      onChange(next);
      setSelectedAvail(new Set());
    } else {
      onChange(value.filter((v) => !ids.has(permissionId(v.entity, v.action))));
      setSelectedAssigned(new Set());
    }
  }

  function moveAll(toAssigned: boolean) {
    if (disabled) return;
    if (toAssigned) {
      const additions = availFiltered.map((p) => ({ entity: p.entity, action: p.action }));
      onChange([...value, ...additions]);
      setSelectedAvail(new Set());
    } else {
      const remove = new Set(assignedFiltered.map((p) => permissionId(p.entity, p.action)));
      onChange(value.filter((v) => !remove.has(permissionId(v.entity, v.action))));
      setSelectedAssigned(new Set());
    }
  }

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
      <PermissionColumn
        title="Available"
        items={availFiltered}
        selected={selectedAvail}
        onSelect={setSelectedAvail}
        filter={filterAvail}
        onFilter={setFilterAvail}
        disabled={disabled}
      />

      <div className="flex flex-col justify-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => move(true)}
          disabled={disabled || selectedAvail.size === 0}
          title="Assign selected"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => moveAll(true)}
          disabled={disabled || availFiltered.length === 0}
          title="Assign all (filtered)"
        >
          All →
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => moveAll(false)}
          disabled={disabled || assignedFiltered.length === 0}
          title="Remove all (filtered)"
        >
          ← All
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => move(false)}
          disabled={disabled || selectedAssigned.size === 0}
          title="Remove selected"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
      </div>

      <PermissionColumn
        title={`Assigned (${value.length})`}
        items={assignedFiltered}
        selected={selectedAssigned}
        onSelect={setSelectedAssigned}
        filter={filterAssigned}
        onFilter={setFilterAssigned}
        disabled={disabled}
        rightAligned
      />
    </div>
  );
}

interface ColProps {
  title: string;
  items: Array<{ entity: string; action: Action; group: string }>;
  selected: Set<string>;
  onSelect: (next: Set<string>) => void;
  filter: string;
  onFilter: (q: string) => void;
  disabled?: boolean;
  rightAligned?: boolean;
}

function PermissionColumn({
  title,
  items,
  selected,
  onSelect,
  filter,
  onFilter,
  disabled,
  rightAligned,
}: ColProps) {
  // Group items by entity-group then by entity
  const byGroup = useMemo(() => {
    const map = new Map<string, Map<string, Action[]>>();
    for (const it of items) {
      if (!map.has(it.group)) map.set(it.group, new Map());
      const inner = map.get(it.group)!;
      if (!inner.has(it.entity)) inner.set(it.entity, []);
      inner.get(it.entity)!.push(it.action);
    }
    return map;
  }, [items]);

  // Entities are COLLAPSED by default. User must click the chevron to expand.
  // We track the inverse ("expanded" set) so initial state is just an empty Set.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleEntity(entity: string) {
    const next = new Set(expanded);
    if (next.has(entity)) next.delete(entity);
    else next.add(entity);
    setExpanded(next);
  }

  function toggleItem(entity: string, action: Action) {
    if (disabled) return;
    const id = permissionId(entity, action);
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelect(next);
  }

  function selectEntity(entity: string, actions: Action[]) {
    if (disabled) return;
    const ids = actions.map((a) => permissionId(entity, a));
    const allSelected = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    if (allSelected) ids.forEach((id) => next.delete(id));
    else ids.forEach((id) => next.add(id));
    onSelect(next);
  }

  return (
    <div className="flex h-[480px] flex-col rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)]">
      <div className="flex-none border-b border-[var(--color-border-subtle)] p-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-bold">{title}</h4>
          <Badge tone="neutral">{items.length}</Badge>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-[var(--color-fg-subtle)]" />
          <Input
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            placeholder="Filter…"
            className="pl-9 h-9"
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-2 scrollbar-thin">
        {byGroup.size === 0 && (
          <p className="px-2 py-6 text-center text-xs text-[var(--color-fg-muted)]">
            {filter ? 'No items match the filter.' : 'No items.'}
          </p>
        )}
        {[...byGroup.entries()].map(([groupLabel, byEntity]) => (
          <div key={groupLabel} className="mb-4">
            <p className="px-2 pb-1 text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-fg-subtle)]">
              {groupLabel}
            </p>
            {[...byEntity.entries()].map(([entity, actions]) => {
              const isExpanded = expanded.has(entity);
              const allSelected = actions.every((a) =>
                selected.has(permissionId(entity, a)),
              );
              return (
                <div key={entity} className="mb-1">
                  <div
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-[var(--color-bg-elevated)]',
                      rightAligned && 'flex-row-reverse',
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => toggleEntity(entity)}
                      className="text-[var(--color-fg-muted)]"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => selectEntity(entity, actions)}
                      disabled={disabled}
                      className="h-3.5 w-3.5 accent-[var(--color-accent)]"
                    />
                    <span className="flex-1 text-xs font-bold text-[var(--color-fg-primary)]">{entityLabel(entity)}</span>
                    <span className="text-[10px] text-[var(--color-fg-muted)]">{actions.length}</span>
                  </div>
                  {isExpanded && (
                    <ul className="ml-6 mt-0.5 space-y-0.5">
                      {actions.map((action) => {
                        const id = permissionId(entity, action);
                        const isSel = selected.has(id);
                        return (
                          <li key={id}>
                            <label
                              className={cn(
                                'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-[var(--color-bg-elevated)]',
                                isSel && 'bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]',
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={() => toggleItem(entity, action)}
                                disabled={disabled}
                                className="h-3 w-3 accent-[var(--color-accent)]"
                              />
                              <span className="flex-1">{action}</span>
                              {action === 'full' && (
                                <Badge tone="warn" className="text-[9px]">expands</Badge>
                              )}
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
